/**
 * DocumentUploadZone - CS11 Document Ingestion Integration
 * 
 * Full integration with CS11 DocumentIngestionOrchestrator backend:
 * - Drag-drop file upload (PDF, PPTX, DOCX, HTML, MD, TXT)
 * - Real-time progress indicators
 * - Extraction preview with C-ID citations
 * - One-click workflow population
 * 
 * Backend API: POST /api/v1/documents/ingest
 * 
 * Created: January 27, 2026
 * Authors: James (Dev), Sally (UX), Winston (Architect)
 */

import React, { useState, useCallback, useRef } from 'react';
import {
  Upload, FileText, CheckCircle2, AlertCircle,
  Loader2, RefreshCw, FileType, Eye, ChevronDown,
  ChevronUp, Copy, ExternalLink, Link2, Globe
} from 'lucide-react';
import { ClassificationPreview } from '../knowledge/ClassificationPreview';
import type { ContentClassificationResult } from '../../types/content-classification';
import { apiRequest } from '../../lib/api';

// Types matching CS11 backend
interface SourceAnchor {
  page?: number;
  paragraph?: number;
  heading?: string;
  text_snippet: string;
}

interface ExtractedField {
  field_name: string;
  value: string | string[];
  confidence: number;
  source_anchor: SourceAnchor;
  citation_id?: string;
}

interface ExtractionResult {
  document_type: string;
  confidence: 'HIGH' | 'MEDIUM' | 'LOW';
  fields: ExtractedField[];
  tags: string[];
  extraction_hints?: string[];
}

interface IngestionProgress {
  stage: 'preprocessing' | 'classifying' | 'extracting' | 'ner' | 'enriching' | 'atlas' | 'complete' | 'error';
  progress: number;
  message: string;
  current_step?: string;
}

interface IngestionResult {
  document_id: string;
  document_type: string;
  workflow_step: number;
  extraction: ExtractionResult;
  workflow_mapping: Record<string, unknown>;
  citations: Array<{
    citation_id: string;
    type: string;
    content: string;
    source: SourceAnchor;
  }>;
  atlas_entities?: string[];
}

interface DocumentUploadZoneProps {
  /** Target workflow step (1-4) */
  targetStep: number;
  /** Callback when extraction completes with mapped workflow data */
  onExtractionComplete: (data: Record<string, unknown>, citations: Array<unknown>) => void;
  /** Callback for status updates */
  onStatusChange?: (status: IngestionProgress) => void;
  /** Allowed document types (defaults to all) */
  allowedTypes?: string[];
  /** Custom class name */
  className?: string;
  /** Whether to show extraction preview */
  showPreview?: boolean;
  /** Whether extraction is disabled */
  disabled?: boolean;
  // ENTITY ATTRIBUTION: Optional entity IDs for RAG/KG filtering (Phase 2.2, Jan 28 2026)
  /** Organization ID (required for multi-tenant isolation) */
  organizationId?: string;
  /** Client Organization ID (for client-scoped content) */
  clientOrganizationId?: string;
  /** UUID of associated partner (for partner-scoped content) */
  clientPartnerId?: string;
  /** UUID of associated vendor (for vendor-scoped content) */
  clientVendorId?: string;
  /** UUID of specific vendor solution */
  vendorSolutionId?: string;
  /** UUID of associated campaign */
  campaignId?: string;
  /** Collection type: vendor, partner, analyst, internal, competitor */
  collectionType?: 'vendor' | 'partner' | 'analyst' | 'internal' | 'competitor';
  /** Document category: governance, client, vendor, partner, solution */
  documentCategory?: string;
  /** Geographic markets: CSV or JSON list of ISO country codes from Step 1 ICP selection */
  geographic_markets?: string | string[];
}

const DOCUMENT_TYPE_LABELS: Record<string, string> = {
  company_profile: 'Company Profile',
  solution_brief: 'Campaign Brief / Pitch Deck',
  case_study: 'Case Study',
  buyer_persona: 'Buyer Persona',
  icp_definition: 'ICP Definition',
  partner_profile: 'Partner Profile',
  campaign_brief: 'Campaign Brief',
  market_analysis: 'Market Analysis',
  competitor_intel: 'Competitive Intelligence',
  product_spec: 'Product Specification',
  press_release: 'Press Release',
  blog_post: 'Blog Post',
  whitepaper: 'Whitepaper',
  datasheet: 'Datasheet',
  email_template: 'Email Template',
  social_post: 'Social Media Content',
  landing_page: 'Landing Page',
  webinar_script: 'Webinar Script',
  video_script: 'Video Script',
  sales_deck: 'Sales Deck',
  proposal: 'Proposal',
  contract: 'Contract',
  invoice: 'Invoice',
  meeting_notes: 'Meeting Notes',
  generic: 'Generic Document'
};

const STAGE_LABELS: Record<string, string> = {
  preprocessing: 'Preprocessing document...',
  classifying: 'Classifying document type...',
  extracting: 'Extracting structured data...',
  ner: 'Identifying named entities...',
  enriching: 'Enriching with C-ID citations...',
  atlas: 'Updating knowledge graph...',
  complete: 'Extraction complete!',
  error: 'Extraction failed'
};

const ALLOWED_EXTENSIONS = ['pdf', 'pptx', 'docx', 'html', 'htm', 'md', 'txt'];
const MAX_FILE_SIZE = 50 * 1024 * 1024; // 50MB

// RAILING 3: Per-file status tracking
interface FileStatus {
  id: string;
  file: File;
  stage: IngestionProgress['stage'];
  progress: number;
  message: string;
  error?: string;
}

export const DocumentUploadZone: React.FC<DocumentUploadZoneProps> = ({
  targetStep,
  onExtractionComplete,
  onStatusChange,
  allowedTypes,
  className = '',
  showPreview = true,
  disabled = false,
  // ENTITY ATTRIBUTION props (Phase 2.2)
  organizationId,
  clientOrganizationId,
  clientPartnerId,
  clientVendorId,
  vendorSolutionId,
  campaignId,
  collectionType = 'internal',
  documentCategory,
  geographic_markets
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const [files, setFiles] = useState<Map<string, File>>(new Map());
  const [queueId, setQueueId] = useState<string>(() => {
    // RAILING 1: Queue durability - generate and restore queue ID from sessionStorage
    const stored = sessionStorage.getItem('uploadQueueId');
    return stored || crypto.randomUUID();
  });
  const [fileStatuses, setFileStatuses] = useState<Map<string, FileStatus>>(new Map());
  const [isProcessingQueue, setIsProcessingQueue] = useState(false);
  const [progress, setProgress] = useState<IngestionProgress | null>(null);
  const [result, setResult] = useState<IngestionResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [expandedFields, setExpandedFields] = useState<Set<string>>(new Set());
  const fileInputRef = useRef<HTMLInputElement>(null);
  const pollIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // Classification state (CCE-009)
  const [classifying, setClassifying] = useState(false);
  const [classification, setClassification] = useState<ContentClassificationResult | null>(null);
  const [classificationError, setClassificationError] = useState<Error | null>(null);

  // CCE-012: Classification preview expansion state
  const [classificationExpanded, setClassificationExpanded] = useState(false);

  // URL extraction state (Phase 10)
  const [activeTab, setActiveTab] = useState<'file' | 'url'>('file');
  const [url, setUrl] = useState('');
  const [urlExtracting, setUrlExtracting] = useState(false);
  const [urlPreview, setUrlPreview] = useState<{
    title: string;
    word_count: number;
    quality_score: number;
    preview: string;
  } | null>(null);

  // Validate file
  const validateFile = useCallback((file: File): string | null => {
    const extension = file.name.split('.').pop()?.toLowerCase();
    if (!extension || !ALLOWED_EXTENSIONS.includes(extension)) {
      return `Invalid file type. Allowed: ${ALLOWED_EXTENSIONS.join(', ')}`;
    }
    if (file.size > MAX_FILE_SIZE) {
      return `File too large. Maximum size: ${MAX_FILE_SIZE / 1024 / 1024}MB`;
    }
    return null;
  }, []);

  // Poll for status
  const pollStatus = useCallback(async (documentId: string) => {
    try {
      const response = await apiRequest(`/api/v1/documents/${documentId}`);
      if (!response.ok) throw new Error('Failed to fetch status');

      const data = await response.json();

      // Map backend status to progress stages and calculate real progress
      let stage: IngestionProgress['stage'] = 'preprocessing';
      let progressPercent = 0;

      if (data.status === 'completed' || data.processing_status === 'rag_processed' || data.processing_status === 'completed') {
        stage = 'complete';
        progressPercent = 100;
      } else if (data.status === 'failed' || data.processing_status === 'failed') {
        stage = 'error';
        progressPercent = 0;
      } else {
        // Map backend processing_status to stages with realistic progress
        switch (data.processing_status || data.status) {
          case 'pending':
          case 'preprocessing':
            stage = 'preprocessing';
            progressPercent = 20;
            break;
          case 'classifying':
            stage = 'classifying';
            progressPercent = 40;
            break;
          case 'extracting':
            stage = 'extracting';
            progressPercent = 60;
            break;
          case 'rag_processing':
          case 'enriching':
            stage = 'enriching';
            progressPercent = 80;
            break;
          case 'atlas_processing':
            stage = 'atlas';
            progressPercent = 90;
            break;
          default:
            // If processing but unknown stage, show mid-progress
            stage = 'preprocessing';
            progressPercent = 50;
        }
      }

      const newProgress: IngestionProgress = {
        stage,
        progress: progressPercent,
        message: data.status_message || STAGE_LABELS[stage] || 'Processing...',
        current_step: data.processing_stage
      };

      setProgress(newProgress);
      onStatusChange?.(newProgress);

      if (data.status === 'completed') {
        // Stop polling
        if (pollIntervalRef.current) {
          clearInterval(pollIntervalRef.current);
          pollIntervalRef.current = null;
        }

        setResult(data.result);
        onExtractionComplete(data.result.workflow_mapping, data.result.citations);

        // CCE-009: Auto-trigger classification after async upload completes
        if (data.result?.document_id) {
          classifyDocument(data.result.document_id);
        }
      } else if (data.status === 'failed') {
        if (pollIntervalRef.current) {
          clearInterval(pollIntervalRef.current);
          pollIntervalRef.current = null;
        }
        setError(data.error_message || 'Extraction failed');
      }
    } catch (err) {
      console.error('Status poll error:', err);
    }
  }, [onExtractionComplete, onStatusChange]);

  // Persist queue durability to sessionStorage
  React.useEffect(() => {
    sessionStorage.setItem('uploadQueueId', queueId);
  }, [queueId]);

  // Upload single file with RAILING 2: Error handling
  const uploadSingleFile = useCallback(async (file: File, fileId: string) => {
    try {
      // Update file status: preprocessing
      setFileStatuses(prev => new Map(prev).set(fileId, {
        id: fileId,
        file,
        stage: 'preprocessing',
        progress: 0,
        message: 'Processing document...'
      }));

      const formData = new FormData();
      formData.append('file', file);
      formData.append('target_step', targetStep.toString());
      if (allowedTypes && allowedTypes.length > 0) {
        formData.append('expected_types', JSON.stringify(allowedTypes));
      }

      // ENTITY ATTRIBUTION: Pass entity IDs for RAG/KG filtering (Phase 2.2)
      if (organizationId) {
        formData.append('organization_id', organizationId);
      }
      if (clientOrganizationId) {
        formData.append('client_organization_id', clientOrganizationId);
      }
      if (clientPartnerId) {
        formData.append('client_partner_id', clientPartnerId);
      }
      if (clientVendorId) {
        formData.append('client_vendor_id', clientVendorId);
      }
      if (vendorSolutionId) {
        formData.append('vendor_solution_id', vendorSolutionId);
      }
      if (campaignId) {
        formData.append('campaign_id', campaignId);
      }
      if (collectionType) {
        formData.append('collection_type', collectionType);
      }
      if (documentCategory) {
        formData.append('document_category', documentCategory);
      }
      // GEOGRAPHIC ATTRIBUTION: Pass geographic markets selected in Step 1 (Jan 31, 2026)
      if (geographic_markets) {
        const geoValue = Array.isArray(geographic_markets)
          ? JSON.stringify(geographic_markets)
          : geographic_markets;
        formData.append('geographic_markets', geoValue);
      }

      const response = await apiRequest('/api/v1/documents/ingest', {
        method: 'POST',
        body: formData
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Upload failed');
      }

      const data = await response.json();

      if (data.success) {
        // Synchronous processing completed
        setFileStatuses(prev => new Map(prev).set(fileId, {
          id: fileId,
          file,
          stage: 'complete',
          progress: 100,
          message: 'Document processed!'
        }));

        // Build result from IngestionResponse fields
        const ingestionResult: IngestionResult = {
          document_id: data.document_id,
          document_type: data.document_type || 'unknown',
          workflow_step: data.workflow_step || 0,
          extraction: { document_type: data.document_type || 'unknown', confidence: 'MEDIUM', fields: [], tags: [] },
          workflow_mapping: data.step_data || {},
          citations: [],
          atlas_entities: []
        };

        // Callback with mapped data
        onExtractionComplete(data.step_data || {}, data.citations || []);

        // CCE-009: Auto-trigger classification
        if (data.document_id) {
          classifyDocument(data.document_id);
        }
      } else if (data.status === 'processing' && data.document_id) {
        // Async mode — start polling for this file
        pollIntervalRef.current = setInterval(() => pollStatus(data.document_id), 1000);
      }
    } catch (err) {
      // RAILING 2: Error handling - log and mark file as failed, continue to next
      const message = err instanceof Error ? err.message : 'Upload failed';
      console.error(`File upload failed: ${file.name}`, err);

      setFileStatuses(prev => new Map(prev).set(fileId, {
        id: fileId,
        file,
        stage: 'error',
        progress: 0,
        message,
        error: message
      }));
    }
  }, [targetStep, allowedTypes, pollStatus, onExtractionComplete, organizationId, clientOrganizationId, clientPartnerId, clientVendorId, vendorSolutionId, campaignId, collectionType, documentCategory, geographic_markets]);

  // Process queue sequentially (RAILING 2: Continue on error)
  const processQueue = useCallback(async (filesToProcess: File[]) => {
    setIsProcessingQueue(true);
    setFileStatuses(new Map());
    setError(null);

    const fileMap = new Map<string, File>();
    const statusMap = new Map<string, FileStatus>();

    // Initialize all files
    filesToProcess.forEach(f => {
      const fileId = crypto.randomUUID();
      fileMap.set(fileId, f);
      statusMap.set(fileId, {
        id: fileId,
        file: f,
        stage: 'preprocessing',
        progress: 0,
        message: 'Pending...'
      });
    });

    setFiles(fileMap);
    setFileStatuses(statusMap);

    // Process each file sequentially
    let completed = 0;
    for (const [fileId, file] of fileMap) {
      // Update overall progress
      setProgress({
        stage: 'preprocessing',
        progress: Math.round((completed / fileMap.size) * 100),
        message: `Processing ${completed + 1}/${fileMap.size}: ${file.name}`
      });

      // Upload this file - catches errors internally
      await uploadSingleFile(file, fileId);
      completed++;
    }

    setIsProcessingQueue(false);

    // Summary: show completion with stats
    const failed = Array.from(statusMap.values()).filter(s => s.stage === 'error').length;
    const message = failed > 0
      ? `Completed with ${failed} error(s)`
      : `All ${fileMap.size} file(s) processed successfully`;

    setProgress({
      stage: 'complete',
      progress: 100,
      message
    });
  }, [uploadSingleFile]);

  // Handle file selection (single or multiple)
  const handleFileSelect = useCallback((selectedFiles: FileList | null) => {
    if (!selectedFiles || selectedFiles.length === 0) return;

    // Validate all files
    const filesToUpload: File[] = [];
    for (let i = 0; i < selectedFiles.length; i++) {
      const file = selectedFiles[i];
      const validationError = validateFile(file);
      if (validationError) {
        setError(`${file.name}: ${validationError}`);
        continue;
      }
      filesToUpload.push(file);
    }

    if (filesToUpload.length === 0) return;

    // Reset and start queue
    setResult(null);
    processQueue(filesToUpload);
  }, [validateFile, processQueue]);

  // Drag and drop handlers
  const handleDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!disabled) setIsDragging(true);
  }, [disabled]);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    if (disabled) return;

    // Accept multiple files from drop
    handleFileSelect(e.dataTransfer.files);
  }, [disabled, handleFileSelect]);

  // File input change handler (handles multiple files)
  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    handleFileSelect(e.target.files);
  }, [handleFileSelect]);

  // Toggle field expansion
  const toggleFieldExpanded = useCallback((fieldName: string) => {
    setExpandedFields(prev => {
      const next = new Set(prev);
      if (next.has(fieldName)) {
        next.delete(fieldName);
      } else {
        next.add(fieldName);
      }
      return next;
    });
  }, []);

  // Reset state
  const handleReset = useCallback(() => {
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
    setFiles(new Map());
    setFileStatuses(new Map());
    setIsProcessingQueue(false);
    setProgress(null);
    setResult(null);
    setError(null);
    setExpandedFields(new Set());
    // CCE-009: Reset classification state
    setClassification(null);
    setClassifying(false);
    setClassificationError(null);
    // CCE-012: Reset expansion state
    setClassificationExpanded(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, []);

  // Copy citation to clipboard
  const copyCitation = useCallback((citationId: string) => {
    navigator.clipboard.writeText(citationId);
    // TODO: Show toast notification
  }, []);

  // CCE-009: Auto-classify document after upload
  const classifyDocument = useCallback(async (documentId: string) => {
    setClassifying(true);
    setClassificationError(null);

    try {
      const response = await apiRequest(`/api/v1/classifications/${documentId}/classify`, {
        method: 'POST',
        body: JSON.stringify({})
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Classification failed');
      }

      const data = await response.json();
      setClassification(data);
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Classification failed');
      setClassificationError(error);
      console.error('Classification error:', err);
    } finally {
      setClassifying(false);
    }
  }, []);

  // Retry classification on error
  const retryClassification = useCallback(() => {
    if (result?.document_id) {
      classifyDocument(result.document_id);
    }
  }, [result, classifyDocument]);

  // Cleanup on unmount
  React.useEffect(() => {
    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
    };
  }, []);

  // URL EXTRACTION HANDLERS (Phase 10)
  const extractUrl = useCallback(async () => {
    setError(null);
    setUrlPreview(null);
    setUrlExtracting(true);

    try {
      const response = await apiRequest('/api/url/extract', {
        method: 'POST',
        body: JSON.stringify({ url })
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || 'Failed to extract URL');
      }

      const data = await response.json();
      setUrlPreview({
        title: data.title || 'Untitled',
        word_count: data.word_count || 0,
        quality_score: data.quality_score || 0,
        preview: data.content.slice(0, 300) || ''
      });
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to extract URL content';
      setError(errorMessage);
    } finally {
      setUrlExtracting(false);
    }
  }, [url]);

  const addUrlToKnowledgeBase = useCallback(async () => {
    setError(null);
    setProgress({ stage: 'preprocessing', progress: 0, message: 'Ingesting URL content...' });

    try {
      const params = new URLSearchParams();
      if (clientPartnerId) params.append('client_partner_id', clientPartnerId);
      if (clientVendorId) params.append('client_vendor_id', clientVendorId);
      if (vendorSolutionId) params.append('vendor_solution_id', vendorSolutionId);
      if (campaignId) params.append('campaign_id', campaignId);

      const response = await apiRequest(`/api/url/extract-and-ingest?${params.toString()}`, {
        method: 'POST',
        body: JSON.stringify({ url })
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || 'Failed to ingest URL');
      }

      const data = await response.json();
      setProgress({ stage: 'complete', progress: 100, message: 'URL content added to knowledge base' });
      setResult({
        document_id: data.document_id,
        document_type: 'blog_post',
        workflow_step: targetStep,
        extraction: {
          document_type: 'blog_post',
          confidence: 'HIGH',
          fields: [],
          tags: []
        },
        workflow_mapping: {},
        citations: []
      });

      // CCE-009: Auto-trigger classification after URL extraction
      if (data.document_id) {
        classifyDocument(data.document_id);
      }
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to ingest URL';
      setError(errorMessage);
      setProgress({ stage: 'error', progress: 0, message: errorMessage });
    }
  }, [url, clientPartnerId, clientVendorId, vendorSolutionId, campaignId, targetStep]);

  const getQualityLabel = (score: number): { label: string; color: string } => {
    if (score >= 80) return { label: 'Excellent', color: 'bg-green-100 text-green-800' };
    if (score >= 60) return { label: 'Good', color: 'bg-emerald-100 text-emerald-800' };
    if (score >= 40) return { label: 'Moderate', color: 'bg-yellow-100 text-yellow-800' };
    if (score >= 20) return { label: 'Minimal', color: 'bg-orange-100 text-orange-800' };
    return { label: 'Poor', color: 'bg-red-100 text-red-800' };
  };

  // Render confidence badge
  const renderConfidenceBadge = (confidence: number) => {
    const color = confidence >= 80 ? 'bg-green-100 text-green-800' :
      confidence >= 60 ? 'bg-yellow-100 text-yellow-800' :
        'bg-red-100 text-red-800';
    return (
      <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${color}`}>
        {confidence}%
      </span>
    );
  };

  // RAILING 3: Render per-file progress queue
  if (isProcessingQueue) {
    return (
      <div className={`${className} p-6 bg-white rounded-xl border border-gray-200`}>
        <div className="mb-4">
          <div className="flex items-center justify-between mb-2">
            <h3 className="font-semibold text-gray-900">Uploading Files</h3>
            <span className="text-sm text-gray-600">{progress ? Math.round(progress.progress) : 0}%</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div
              className="bg-emerald-600 h-2 rounded-full transition-all duration-300"
              style={{ width: `${progress ? Math.ceil(progress.progress) : 0}%` }}
            />
          </div>
          {progress && (
            <p className="text-xs text-gray-600 mt-2">{progress.message}</p>
          )}
        </div>

        {/* Per-file status list */}
        <div className="space-y-2 max-h-64 overflow-y-auto">
          {Array.from(fileStatuses.values()).map(status => (
            <div key={status.id} className="flex items-center gap-3 p-2 bg-gray-50 rounded-lg">
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 truncate">{status.file.name}</p>
                <p className="text-xs text-gray-600">{status.message}</p>
              </div>
              <div className="text-right flex-shrink-0">
                {status.stage === 'complete' && <span className="text-green-600 font-semibold">✓</span>}
                {status.stage === 'error' && <span className="text-red-600 font-semibold">✕</span>}
                {status.stage !== 'complete' && status.stage !== 'error' && (
                  <Loader2 className="w-4 h-4 text-emerald-600 animate-spin" />
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // Render upload zone
  if (files.size === 0 && !progress) {
    return (
      <div className={className}>
        {/* Tab Navigation */}
        <div className="flex border-b border-gray-200 mb-4">
          <button
            onClick={() => setActiveTab('file')}
            className={`
              flex items-center gap-2 px-4 py-2 font-medium text-sm transition-colors
              ${activeTab === 'file'
                ? 'text-emerald-700 border-b-2 border-emerald-600'
                : 'text-gray-500 hover:text-gray-700'}
            `}
          >
            <Upload className="w-4 h-4" />
            From File
          </button>
          <button
            onClick={() => setActiveTab('url')}
            className={`
              flex items-center gap-2 px-4 py-2 font-medium text-sm transition-colors
              ${activeTab === 'url'
                ? 'text-emerald-700 border-b-2 border-emerald-600'
                : 'text-gray-500 hover:text-gray-700'}
            `}
          >
            <Link2 className="w-4 h-4" />
            From URL
          </button>
        </div>

        {/* File Upload Tab */}
        {activeTab === 'file' && (
          <div
            onDragEnter={handleDragEnter}
            onDragLeave={handleDragLeave}
            onDragOver={handleDragOver}
            onDrop={handleDrop}
            onClick={() => !disabled && fileInputRef.current?.click()}
            className={`
              relative border-2 border-dashed rounded-xl p-8 text-center cursor-pointer
              transition-all duration-200
              ${isDragging ? 'border-emerald-500 bg-emerald-50' : 'border-emerald-200/80 hover:border-emerald-400 hover:bg-emerald-50/60'}
              ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
            `}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept={ALLOWED_EXTENSIONS.map(ext => `.${ext}`).join(',')}
              onChange={handleInputChange}
              className="hidden"
              disabled={disabled}
              multiple
            />

            <div className="flex flex-col items-center">
              <div className={`
                w-16 h-16 rounded-full flex items-center justify-center mb-4
                ${isDragging ? 'bg-emerald-200' : 'bg-emerald-100'}
              `}>
                <Upload className={`w-8 h-8 ${isDragging ? 'text-emerald-800' : 'text-emerald-600'}`} />
              </div>

              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                {isDragging ? 'Drop your document here' : 'Upload a document'}
              </h3>

              <p className="text-sm text-gray-600 mb-4">
                Drag and drop or click to browse
              </p>

              <div className="flex flex-wrap justify-center gap-2 text-xs text-gray-500">
                {ALLOWED_EXTENSIONS.map(ext => (
                  <span key={ext} className="rounded-md border border-emerald-100 bg-emerald-50/80 px-2 py-1 text-emerald-900/80">
                    .{ext.toUpperCase()}
                  </span>
                ))}
              </div>

              <p className="text-xs text-gray-400 mt-3">
                Max file size: 50MB • AI will extract and populate Step {targetStep}
              </p>
            </div>
          </div>
        )}

        {/* URL Extraction Tab */}
        {activeTab === 'url' && (
          <div className="space-y-4">
            <div className="border-2 border-dashed rounded-xl p-6 border-gray-300">
              <div className="flex flex-col items-center mb-4">
                <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mb-4 ring-2 ring-emerald-200/60">
                  <Globe className="w-8 h-8 text-emerald-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  Extract content from URL
                </h3>
                <p className="text-sm text-gray-600 text-center">
                  10x faster than download + upload. SSRF-protected.
                </p>
              </div>

              <div className="flex gap-2">
                <input
                  type="url"
                  value={url}
                  onChange={(e) => setUrl(e.target.value)}
                  placeholder="https://example.com/article"
                  className="flex-1 rounded-xl border border-emerald-200/80 px-4 py-2 focus:border-emerald-400 focus:outline-none focus:ring-2 focus:ring-emerald-500/30"
                  disabled={disabled || urlExtracting}
                />
                <button
                  onClick={extractUrl}
                  disabled={!url || disabled || urlExtracting}
                  className="flex items-center gap-2 rounded-xl border border-emerald-600 bg-emerald-600 px-6 py-2 font-semibold text-white shadow-sm transition-all hover:border-emerald-700 hover:bg-emerald-700 hover:shadow-md active:translate-y-px disabled:cursor-not-allowed disabled:bg-gray-300 disabled:border-gray-300"
                >
                  {urlExtracting ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Extracting...
                    </>
                  ) : (
                    <>
                      <Eye className="w-4 h-4" />
                      Preview
                    </>
                  )}
                </button>
              </div>
            </div>

            {/* URL Preview Card */}
            {urlPreview && (
              <div className="border border-gray-200 rounded-xl p-4 bg-white">
                <div className="flex items-start justify-between mb-3">
                  <h4 className="font-semibold text-gray-900">{urlPreview.title}</h4>
                  <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${getQualityLabel(urlPreview.quality_score).color}`}>
                    {getQualityLabel(urlPreview.quality_score).label}
                  </span>
                </div>

                <p className="text-sm text-gray-600 mb-3">
                  {urlPreview.preview}...
                </p>

                <div className="flex items-center justify-between text-xs text-gray-500 mb-3">
                  <span>{urlPreview.word_count} words</span>
                  <span>Quality: {urlPreview.quality_score}/100</span>
                </div>

                <button
                  onClick={addUrlToKnowledgeBase}
                  className="flex w-full items-center justify-center gap-2 rounded-xl border border-emerald-600 bg-gradient-to-r from-emerald-600 to-teal-600 px-4 py-2.5 font-semibold text-white shadow-md transition-all hover:from-emerald-700 hover:to-teal-700 hover:shadow-lg active:translate-y-px"
                >
                  <CheckCircle2 className="w-4 h-4" />
                  Add to Knowledge Base
                </button>
              </div>
            )}
          </div>
        )}

        {error && (
          <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2">
            <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
            <span className="text-sm text-red-700">{error}</span>
          </div>
        )}
      </div>
    );
  }

  // Render queue completion state
  if (progress && progress.stage === 'complete' && files.size > 0) {
    const successCount = Array.from(fileStatuses.values()).filter(s => s.stage === 'complete').length;
    const failCount = Array.from(fileStatuses.values()).filter(s => s.stage === 'error').length;

    return (
      <div className={`${className} p-6 bg-white rounded-xl border border-gray-200`}>
        <div className="flex items-center gap-4 mb-4">
          <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center">
            <CheckCircle2 className="w-6 h-6 text-green-600" />
          </div>
          <div className="flex-1">
            <h3 className="font-semibold text-gray-900">Upload Complete</h3>
            <p className="text-sm text-gray-600">
              {successCount}/{files.size} successful{failCount > 0 ? `, ${failCount} failed` : ''}
            </p>
          </div>
        </div>

        {/* Summary list */}
        <div className="space-y-2 max-h-32 overflow-y-auto mb-4">
          {Array.from(fileStatuses.values()).map(status => (
            <div key={status.id} className="flex items-center gap-2 text-sm">
              {status.stage === 'complete' ? (
                <><span className="text-green-600">✓</span> {status.file.name}</>
              ) : (
                <><span className="text-red-600">✕</span> {status.file.name}</>
              )}
            </div>
          ))}
        </div>

        <button
          onClick={handleReset}
          className="flex w-full items-center justify-center gap-2 rounded-xl border border-emerald-600 bg-emerald-600 px-4 py-2.5 font-semibold text-white shadow-sm transition-all hover:border-emerald-700 hover:bg-emerald-700 hover:shadow-md active:translate-y-px"
        >
          <Upload className="w-4 h-4" />
          Upload More Files
        </button>
      </div>
    );
  }

  // Render error state
  if (progress?.stage === 'error' || error) {
    return (
      <div className={`${className} p-6 bg-red-50 rounded-xl border border-red-200`}>
        <div className="flex items-center gap-4 mb-4">
          <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center">
            <AlertCircle className="w-6 h-6 text-red-600" />
          </div>
          <div className="flex-1">
            <h3 className="font-semibold text-red-900">Extraction Failed</h3>
            <p className="text-sm text-red-700">{error || progress?.message}</p>
          </div>
        </div>

        <button
          onClick={handleReset}
          className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-white border border-red-300 rounded-lg text-red-700 hover:bg-red-50 transition-colors"
        >
          <RefreshCw className="w-4 h-4" />
          Try Again
        </button>
      </div>
    );
  }

  // Render single result with extraction preview (for backward compatibility)
  if (result && showPreview && files.size === 0) {
    return (
      <div className={`${className} bg-white rounded-xl border border-gray-200 overflow-hidden`}>
        {/* Header */}
        <div className="p-4 bg-gradient-to-r from-green-50 to-emerald-50 border-b border-green-100">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                <CheckCircle2 className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900">
                  {DOCUMENT_TYPE_LABELS[result.document_type] || result.document_type}
                </h3>
                <p className="text-sm text-gray-600">
                  {result.extraction.fields.length} fields extracted • {result.citations.length} citations
                </p>
              </div>
            </div>
            <button
              onClick={handleReset}
              className="px-3 py-1.5 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
            >
              Upload Different
            </button>
          </div>
        </div>

        {/* Extracted fields */}
        <div className="p-4 max-h-96 overflow-y-auto">
          <div className="space-y-3">
            {result.extraction.fields.map((field) => {
              const isExpanded = expandedFields.has(field.field_name);
              const value = Array.isArray(field.value) ? field.value.join(', ') : field.value;

              return (
                <div
                  key={field.field_name}
                  className="border border-gray-200 rounded-lg overflow-hidden"
                >
                  <button
                    onClick={() => toggleFieldExpanded(field.field_name)}
                    className="w-full flex items-center justify-between p-3 hover:bg-gray-50 transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <FileType className="w-4 h-4 text-gray-400" />
                      <span className="font-medium text-gray-900">
                        {field.field_name.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}
                      </span>
                      {renderConfidenceBadge(field.confidence)}
                    </div>
                    {isExpanded ? (
                      <ChevronUp className="w-4 h-4 text-gray-400" />
                    ) : (
                      <ChevronDown className="w-4 h-4 text-gray-400" />
                    )}
                  </button>

                  {isExpanded && (
                    <div className="px-3 pb-3 space-y-2">
                      <div className="p-2 bg-gray-50 rounded text-sm text-gray-700">
                        {value}
                      </div>

                      {field.citation_id && (
                        <div className="flex items-center gap-2 text-xs">
                          <span className="text-gray-500">Citation:</span>
                          <code className="rounded bg-emerald-50 px-2 py-0.5 font-mono text-emerald-800">
                            {field.citation_id}
                          </code>
                          <button
                            onClick={(e) => { e.stopPropagation(); copyCitation(field.citation_id!); }}
                            className="p-1 hover:bg-gray-200 rounded"
                            title="Copy citation ID"
                          >
                            <Copy className="w-3 h-3 text-gray-400" />
                          </button>
                        </div>
                      )}

                      {field.source_anchor?.text_snippet && (
                        <div className="text-xs text-gray-500 italic">
                          Source: "{field.source_anchor.text_snippet.slice(0, 100)}..."
                          {field.source_anchor.page && ` (Page ${field.source_anchor.page})`}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Footer with action */}
        <div className="p-4 bg-gray-50 border-t border-gray-200">
          <p className="text-sm text-gray-600 mb-3">
            These extracted values will populate Step {targetStep}. You can review and edit them after applying.
          </p>
          <button
            onClick={() => onExtractionComplete(result.workflow_mapping, result.citations)}
            className="flex w-full items-center justify-center gap-2 rounded-xl border border-emerald-600 bg-gradient-to-r from-emerald-600 to-teal-600 px-4 py-3 font-semibold text-white shadow-md transition-all hover:from-emerald-700 hover:to-teal-700 hover:shadow-lg active:translate-y-px"
          >
            <CheckCircle2 className="w-5 h-5" />
            Apply to Step {targetStep}
          </button>
        </div>

        {/* CCE-012: Classification Status Inline */}
        <div className="border-t border-gray-200">
          <div className="p-4 bg-gray-50">
            {/* Status Header with Inline Indicator (AC: 1, 6) */}
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold text-gray-900">Content Classification</h3>

              {/* Classification Status Indicator (AC: 1, 6) */}
              {classifying && (
                <div className="flex items-center gap-2 text-sm">
                  <Loader2 className="w-4 h-4 animate-spin text-emerald-600" />
                  <span className="text-gray-600">Classifying...</span>
                </div>
              )}

              {classification && !classifying && !classificationError && (
                <div className="flex items-center gap-2">
                  {/* Overall Confidence Badge (AC: 4) */}
                  <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${classification.overall_confidence >= 0.8
                      ? 'bg-green-100 text-green-800'
                      : classification.overall_confidence >= 0.6
                        ? 'bg-yellow-100 text-yellow-800'
                        : 'bg-red-100 text-red-800'
                    }`}>
                    {Math.round(classification.overall_confidence * 100)}% Confidence
                  </span>

                  {/* Classified Success Icon (AC: 6) */}
                  <div className="flex items-center gap-1.5 text-green-600">
                    <CheckCircle2 className="w-4 h-4" />
                    <span className="text-xs font-medium">Classified</span>
                  </div>
                </div>
              )}
            </div>

            {/* Needs Review Badge Inline (AC: 2, 5) */}
            {classification?.requires_human_review && (
              <div className="mb-3 p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-2 transition-all duration-200">
                <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold bg-amber-100 text-amber-900">
                      Needs Review
                    </span>
                  </div>
                  <p className="text-xs text-amber-700 mt-1">
                    Low confidence detected. Please{' '}
                    <a
                      href="/knowledge/review"
                      className="underline hover:text-amber-900 font-medium"
                    >
                      review later
                    </a>
                    {' '}in the Review Queue.
                  </p>
                </div>
              </div>
            )}

            {/* Classification Error State (AC: 6) */}
            {classificationError && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg transition-all duration-200">
                <div className="flex items-start gap-2">
                  <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <p className="text-sm text-red-700">{classificationError.message}</p>
                    <button
                      onClick={retryClassification}
                      className="mt-2 text-sm text-red-600 hover:text-red-800 font-medium transition-colors"
                    >
                      Retry Classification
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Expandable ClassificationPreview (AC: 3, 7) */}
            {classification && !classifying && !classificationError && (
              <div>
                {/* Expand/Collapse Toggle (AC: 3) */}
                <button
                  onClick={() => setClassificationExpanded(!classificationExpanded)}
                  className="w-full flex items-center justify-between p-3 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  <span className="text-sm font-medium text-gray-700">
                    {classificationExpanded ? 'Hide' : 'View'} All 9 Classification Facets
                  </span>
                  {classificationExpanded ? (
                    <ChevronUp className="w-4 h-4 text-gray-400" />
                  ) : (
                    <ChevronDown className="w-4 h-4 text-gray-400" />
                  )}
                </button>

                {/* Smooth Expansion Animation (AC: 7) */}
                <div
                  className={`
                    overflow-hidden transition-all duration-300 ease-in-out
                    ${classificationExpanded ? 'max-h-[2000px] opacity-100 mt-3' : 'max-h-0 opacity-0'}
                  `}
                >
                  <ClassificationPreview
                    classification={classification}
                    readOnly={true}
                    showConfidence={true}
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Render minimal complete state (no preview)
  return (
    <div className={`${className} p-4 bg-green-50 rounded-xl border border-green-200`}>
      <div className="flex items-center gap-3">
        <CheckCircle2 className="w-6 h-6 text-green-600" />
        <div className="flex-1">
          <p className="font-medium text-green-900">Document processed successfully</p>
          <p className="text-sm text-green-700">
            {DOCUMENT_TYPE_LABELS[result?.document_type || ''] || 'Document'} extracted
          </p>
        </div>
        <button
          onClick={handleReset}
          className="px-3 py-1.5 text-sm text-green-700 hover:bg-green-100 rounded-lg transition-colors"
        >
          Upload Another
        </button>
      </div>
    </div>
  );
};

export default DocumentUploadZone;
